<template>
  <app-drawer :model-value="modelValue" :persistent="false" size="sm"
              :cancel-disabled="loading"
              :ok-text="computedConfirmText" :ok-loading="loading" :ok-disabled="confirmDisabled"
              @update:model-value="$emit('close', 'outside')"
              @cancel="$emit('close', 'cancel')"
              @ok="$emit('confirm')"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <!-- @slot recipients picker, pane-specific controls and optional message -->
      <slot name="body"></slot>
    </template>
  </app-drawer>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

/**
 * Shell shared by every sharing pane: an AppDrawer sized and configured the same way, reusing
 * the drawer's own footer. Consumers bring their title, their locator and their form.
 *
 * The footer is AppDrawer's default one rather than a custom slot, because a sharing pane
 * treats its header button and its cancel button alike: both simply close. FiltersDrawer cannot
 * do the same, since there the footer's left action clears the filters instead of closing, and
 * AppDrawer reports both through a single `cancel` event.
 *
 * The body is deliberately left whole to the consumer: the recipients picker is configured
 * differently by each pane, and each one interleaves its own controls (format radios, share
 * type, document chips) between the picker and the optional message.
 *
 * Closing is reported as a single `close` event carrying its source, as in FiltersDrawer: the
 * cancel button and the header button both mean 'cancel', an outside interaction means
 * 'outside'. No sharing pane distinguishes them today, so all four ignore the argument.
 *
 * `data-locator` is not declared as a prop: it falls through to the drawer, which forwards it
 * through useDataLocator like every other consumer.
 *
 * @displayName ShareDrawer
 */
export default defineComponent({
  name: 'ShareDrawer',
  props: {
    /**
     * @model
     * Visibility state of the drawer
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Drawer title, expected to match the label of the action that opens it
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Sharing in progress: shows the confirm button as loading and locks the cancel button
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Blocks the confirm button, typically while the form is incomplete or invalid
     */
    confirmDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Label of the confirm button, `common.share` unless the pane does something else
     */
    confirmText: {
      type: String,
      default: undefined,
    },
  },
  emits: ['close', 'confirm'],
  computed: {
    computedConfirmText (): string {
      return this.confirmText ?? this.$t('common.share')
    },
  },
})
</script>
